import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from 'react-oidc-context';
import { useAuthSync } from '../../../hooks/useAuthSync';
import Odontogram from '../dentalNotes/Odontogram';
import Button from '../../common/Button';
import PatientSectionHeader from '../common/PatientSectionHeader';
import PatientService from '../../../services/patientService';
import ProcedureService from '../../../services/procedureService';
import OrganizationService from '../../../services/organizationService';
import TreatmentPlanService from '../../../services/treatmentPlanService';
import ProfileService from '../../../services/profileService';
import PatientOdontogramService from '../../../services/patientOdontogramService';
import { printTreatmentPlan } from '../../../utils/treatmentPlanPrintUtils';
import { getSurfaceName, SURFACE_KEYS, inferOdontogramMode } from '../../../utils/dentalSurfaceUtils';
import ToastContainer from '../../common/ToastContainer';
import { useToast } from '../../../hooks/useToast';
import './TreatmentPlan.css';



// Simplified dropdown component for procedures
const SimpleProcedureDropdown = ({ procedures, selectedProcedure, onSelect, placeholder, disabled }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const triggerRef = useRef(null);
  const menuRef = useRef(null);

  const filteredProcedures = procedures.filter(procedure =>
    procedure.name.toLowerCase().includes(searchTerm.toLowerCase())
  ).slice(0, 10);

  const handleSelect = (procedure) => {
    onSelect(procedure);
    setIsOpen(false);
    setSearchTerm('');
  };

  // Calculate position when opened (needed because dropdown renders outside modal via Portal)
  useEffect(() => {
    if (isOpen && triggerRef.current && menuRef.current) {
      const updatePosition = () => {
        if (triggerRef.current && menuRef.current) {
          const rect = triggerRef.current.getBoundingClientRect();
          menuRef.current.style.top = `${rect.bottom + 4}px`;
          menuRef.current.style.left = `${rect.left}px`;
          menuRef.current.style.width = `${rect.width}px`;
        }
      };
      
      updatePosition();
      window.addEventListener('scroll', updatePosition, true);
      window.addEventListener('resize', updatePosition);
      
      return () => {
        window.removeEventListener('scroll', updatePosition, true);
        window.removeEventListener('resize', updatePosition);
      };
    }
  }, [isOpen]);

  // Close dropdown when clicking outside
  useEffect(() => {
    if (!isOpen) return;
    
    const handleClick = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target) &&
          triggerRef.current && !triggerRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    
    setTimeout(() => document.addEventListener('click', handleClick), 0);
    return () => document.removeEventListener('click', handleClick);
  }, [isOpen]);

  const dropdownMenu = isOpen ? (
    <div className="simple-dropdown-menu" ref={menuRef}>
      <div className="search-input">
        <input
          type="text"
          placeholder="Buscar procedimiento..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          autoFocus
        />
      </div>
      <div className="dropdown-options">
        {filteredProcedures.map(procedure => (
          <div
            key={procedure.id}
            className="dropdown-option"
            onClick={() => handleSelect(procedure)}
          >
            {procedure.name}
          </div>
        ))}
        {filteredProcedures.length === 0 && (
          <div className="no-results">No se encontraron procedimientos</div>
        )}
      </div>
    </div>
  ) : null;

  return (
    <div className="simple-dropdown">
      <div
        ref={triggerRef}
        className="dropdown-trigger"
        onClick={() => !disabled && setIsOpen(!isOpen)}
      >
        <span className={selectedProcedure ? 'selected' : 'placeholder'}>
          {selectedProcedure ? selectedProcedure.name : placeholder}
        </span>
        <i className={`fas fa-chevron-${isOpen ? 'up' : 'down'}`}></i>
      </div>

      {/* Render dropdown using Portal outside the modal */}
      {isOpen && typeof document !== 'undefined' && createPortal(
        dropdownMenu,
        document.body
      )}
    </div>
  );
};

const TreatmentPlan = () => {
  const { patientId, planId } = useParams();
  const navigate = useNavigate();
  const auth = useAuth();
  const location = useLocation();
  const { getUserDisplayName, getUserId } = useAuthSync();
  const dateInputRef = useRef(null);
  const { toasts, removeToast, success: showSuccess, error: showError } = useToast();

  // Determinar si estamos en modo edición
  const isEditMode = location.pathname.includes('/edit');
  const isViewMode = planId && !isEditMode;

  // Estados principales
  const [patient, setPatient] = useState(null);
  const [organization, setOrganization] = useState(null);
  const [doctor, setDoctor] = useState(null);
  const [procedures, setProcedures] = useState([]);

  // Estados para el odontograma
  const [selected, setSelected] = useState({}); // { toothLabel: { surface: 'blue' } } - selección temporal actual
  const [addedProcedures, setAddedProcedures] = useState([]); // Array de procedimientos agregados
  // Which arch set the odontogram shows: 'adult' | 'pediatric'. Selections are keyed by
  // FDI label, so a plan can hold permanent and primary teeth at the same time (mixed dentition).
  const [odontogramMode, setOdontogramMode] = useState('adult');

  const [treatmentPlan, setTreatmentPlan] = useState({
    name: '',
    date: new Date().toISOString().split('T')[0],
    notes: '',
    totalCost: 0
  });

  // Estados de carga
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingProcedures, setIsLoadingProcedures] = useState(false);

  // Estados del modal
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingProcedure, setEditingProcedure] = useState(null);
  const [modalProcedure, setModalProcedure] = useState({
    procedureId: '',
    name: '',
    amount: '',
    quantity: '',
    notes: '',
    total: 0,
    discountType: '',      // '' | 'amount' | 'percent'
    discountValue: ''      // monto en $ o porcentaje 0-100
  });

  // Fix date input to enable native calendar picker
  useEffect(() => {
    const setupDateInput = () => {
      if (dateInputRef.current && !isViewMode) {
        const dateInput = dateInputRef.current;
        dateInput.style.position = 'relative';
        dateInput.style.overflow = 'visible';
        // Force webkit appearance for proper native controls
        dateInput.style.webkitAppearance = 'auto';
        dateInput.style.appearance = 'auto';
        // Remove any attributes that might prevent picker
        dateInput.removeAttribute('readonly');
        dateInput.setAttribute('autocomplete', 'off');
      }
    };

    // Setup immediately and after a brief delay to ensure DOM is ready
    setupDateInput();
    const timeoutId = setTimeout(setupDateInput, 100);
    
    return () => clearTimeout(timeoutId);
  }, [isViewMode]);

  // Handle date input focus to fix positioning issues
  const handleDateInputFocus = (e) => {
    if (isViewMode) return;
    const input = e.target;
    const formGroup = input.closest('.form-group');
    
    // Add focus classes for CSS targeting
    if (formGroup) {
      formGroup.classList.add('datetime-focused');
    }
    
    // Ensure proper stacking and positioning
    setTimeout(() => {
      input.style.position = 'relative';
      input.style.zIndex = '99999';
      if (formGroup) {
        formGroup.style.overflow = 'visible';
        formGroup.style.zIndex = '99999';
      }
    }, 0);
  };

  const handleDateInputBlur = (e) => {
    if (isViewMode) return;
    const input = e.target;
    const formGroup = input.closest('.form-group');
    
    // Remove focus classes
    if (formGroup) {
      formGroup.classList.remove('datetime-focused');
    }
    
    // Reset styles
    setTimeout(() => {
      input.style.position = '';
      input.style.zIndex = '';
      if (formGroup) {
        formGroup.style.overflow = '';
        formGroup.style.zIndex = '';
      }
    }, 100);
  };

  // Handle clicks on date input to manually trigger picker
  const handleDateInputClick = (e) => {
    if (isViewMode) return;
    const input = e.target;
    
    // Ensure the input is focused first
    if (document.activeElement !== input) {
      input.focus();
    }
    
    // Small delay to ensure focus is set, then try to trigger picker
    setTimeout(() => {
      try {
        if (input.type === 'date') {
          // For modern browsers with showPicker API
          if ('showPicker' in input && typeof input.showPicker === 'function') {
            input.showPicker();
          }
        }
      } catch (error) {
        console.log('Could not trigger date picker:', error);
      }
    }, 10);
  };

  // Cargar datos del paciente y plan de tratamiento si es necesario
  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoading(true);
        const token = auth.user?.access_token;
        // Cargar datos del paciente, organización y doctor
        const userId = auth.user?.profile?.sub || auth.user?.sub;
        const [patientData, organizationData, doctorData] = await Promise.all([
          PatientService.getPatientById(patientId, token),
          userId ? OrganizationService.getCurrentUserOrganization(userId, token) : Promise.resolve(null),
          userId ? ProfileService.getUserProfile(userId, token).catch(err => {
            console.warn('Could not fetch user profile:', err);
            return null;
          }) : Promise.resolve(null)
        ]);
        setPatient(patientData);
        setOrganization(organizationData);
        setDoctor(doctorData);

        // Default the odontogram to the mode saved on the patient's odontogram (if any)
        const patientOdontogramMode = await PatientOdontogramService.getPatientOdontogram(patientId, token)
          .then(data => data?.mode || null)
          .catch(() => null);
        let initialMode = patientOdontogramMode || 'adult';

        // Si estamos en modo edición o vista, cargar el plan de tratamiento
        if (planId && (isEditMode || isViewMode)) {
          const planData = await TreatmentPlanService.getTreatmentPlanById(planId, token);
          if (planData.data) {
            const plan = planData.data;
            setTreatmentPlan({
              name: plan.name,
              date: plan.date.split('T')[0],
              selectedProcedures: plan.selectedProcedures || [],
              notes: plan.notes || '',
              totalCost: plan.totalCost || 0
            });
            setAddedProcedures(plan.selectedProcedures || []);
            // Open the arch set that actually has teeth in this plan
            initialMode = inferOdontogramMode(plan.selectedProcedures || []) || initialMode;
          }
        }
        setOdontogramMode(initialMode);
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setIsLoading(false);
      }
    };

    if (patientId && auth.user?.access_token) {
      fetchData();
    }
  }, [patientId, planId, isEditMode, isViewMode, auth.user?.access_token, auth.user?.sub, auth.user?.profile?.sub]);

  // Cargar procedimientos
  useEffect(() => {
    const fetchProcedures = async () => {
      try {
        setIsLoadingProcedures(true);
        const token = auth.user?.access_token;
        const proceduresData = await ProcedureService.getAllProcedures(token);
        setProcedures(proceduresData.data || proceduresData || []);
      } catch (error) {
        console.error('Error fetching procedures:', error);
      } finally {
        setIsLoadingProcedures(false);
      }
    };

    if (auth.user?.access_token) {
      fetchProcedures();
    }
  }, [auth.user?.access_token]);

  // Función para verificar si una superficie ya está en procedimientos agregados
  const isSurfaceInAddedProcedures = (toothLabel, surface) => {
    return addedProcedures.some(procedure =>
      procedure.selectedTeeth && procedure.selectedTeeth[toothLabel] && procedure.selectedTeeth[toothLabel][surface]
    );
  };

  // Manejar selección de dientes (flujo original: primero dientes, después procedimiento)
  const toggleSelection = (toothLabel, surface) => {
    if (!SURFACE_KEYS.includes(surface)) return;

    // Verificar si esta superficie ya está en un procedimiento agregado anteriormente
    const isInAddedProcedures = isSurfaceInAddedProcedures(toothLabel, surface);

    // Si está en procedimientos agregados, no permitir modificar
    if (isInAddedProcedures) {
      return;
    }

    setSelected(prev => {
      const toothData = prev[toothLabel] || {};
      const next = { ...prev };

      if (toothData[surface]) {
        // Si ya está marcada, la desmarcamos
        const newToothData = { ...toothData };
        delete newToothData[surface];

        if (Object.keys(newToothData).length === 0) {
          delete next[toothLabel];
        } else {
          next[toothLabel] = newToothData;
        }
      } else {
        // Si no está marcada, la marcamos con el color azul
        next[toothLabel] = {
          ...toothData,
          [surface]: 'blue'
        };
      }

      return next;
    });
  };

  // Abrir modal para agregar procedimiento
  const handleAddProcedure = () => {
    // Verificar que hay dientes seleccionados
    if (Object.keys(selected).length === 0) {
      alert('Por favor selecciona al menos un diente antes de agregar un procedimiento');
      return;
    }

    setEditingProcedure(null);
    setModalProcedure({
      procedureId: '',
      name: '',
      amount: '',
      quantity: '',
      notes: '',
      total: 0,
      discountType: '',
      discountValue: ''
    });
    setIsModalOpen(true);
  };

  // Abrir modal para editar procedimiento
  const handleEditProcedure = (procedure) => {
    setEditingProcedure(procedure);
    setSelected(JSON.parse(JSON.stringify(procedure.selectedTeeth || {})));
    setModalProcedure({
      procedureId: procedure.procedureId,
      name: procedure.name,
      amount: procedure.amount,
      quantity: procedure.quantity,
      notes: procedure.notes,
      total: procedure.total,
      discountType: procedure.discountType ?? '',
      discountValue: procedure.discountValue ?? ''
    });
    setIsModalOpen(true);
  };

  // Calcular subtotal, descuento y total del procedimiento en el modal
  const getModalTotals = (proc) => {
    const subtotal = (Number(proc.amount) || 0) * (Number(proc.quantity) || 0);
    let discountAmount = 0;
    if (proc.discountType === 'amount') {
      discountAmount = Math.min(Number(proc.discountValue) || 0, subtotal);
    } else if (proc.discountType === 'percent') {
      const pct = Math.min(Math.max(Number(proc.discountValue) || 0, 0), 100);
      discountAmount = subtotal * (pct / 100);
    }
    const total = Math.max(0, subtotal - discountAmount);
    return { subtotal, discountAmount, total };
  };

  // Manejar selección de procedimiento en el modal
  const handleProcedureSelect = (procedure) => {
    if (procedure) {
      const cost = procedure.cost || 0;
      setModalProcedure(prev => {
        const quantity = prev.quantity || 1;
        const next = {
          ...prev,
          procedureId: procedure.id,
          name: procedure.name,
          amount: cost,
          quantity: quantity,
          discountType: prev.discountType ?? '',
          discountValue: prev.discountValue ?? ''
        };
        const { total } = getModalTotals(next);
        return { ...next, total };
      });
    }
  };

  // Actualizar cantidad en el modal
  const handleQuantityChange = (quantity) => {
    const newQuantity = quantity === '' ? '' : parseInt(quantity) || 0;
    setModalProcedure(prev => {
      const next = { ...prev, quantity: newQuantity };
      const { total } = getModalTotals(next);
      return { ...next, total };
    });
  };

  // Actualizar descuento y recalcular total
  const handleDiscountChange = (discountType, discountValue) => {
    setModalProcedure(prev => {
      const next = {
        ...prev,
        discountType: discountType,
        discountValue: discountValue
      };
      const { total } = getModalTotals(next);
      return { ...next, total };
    });
  };

  // Guardar procedimiento desde el modal
  const handleSaveProcedure = () => {
    if (!modalProcedure.procedureId || !modalProcedure.name) {
      alert('Por favor selecciona un procedimiento');
      return;
    }

    const selectedSnapshot = Object.keys(selected).length > 0
      ? selected
      : (editingProcedure?.selectedTeeth || {});

    const procedureData = {
      id: editingProcedure ? editingProcedure.id : Date.now().toString(),
      procedureId: modalProcedure.procedureId,
      name: modalProcedure.name,
      amount: modalProcedure.amount,
      quantity: modalProcedure.quantity,
      notes: modalProcedure.notes,
      total: modalProcedure.total,
      discountType: modalProcedure.discountType || undefined,
      discountValue: modalProcedure.discountValue !== '' ? modalProcedure.discountValue : undefined,
      selectedTeeth: JSON.parse(JSON.stringify(selectedSnapshot)) // Guardar dientes seleccionados
    };

    if (editingProcedure) {
      // Editar procedimiento existente
      setAddedProcedures(prev => prev.map(proc =>
        proc.id === editingProcedure.id ? procedureData : proc
      ));
    } else {
      // Agregar nuevo procedimiento
      setAddedProcedures(prev => [...prev, procedureData]);
    }

    // Limpiar selección de dientes después de agregar procedimiento
    setSelected({});

    setIsModalOpen(false);
  };

  // Eliminar un procedimiento de la lista
  const handleRemoveProcedure = (procedureId) => {
    setAddedProcedures(prev => prev.filter(proc => proc.id !== procedureId));
  };


  // Combinar todas las selecciones para mostrar en el odontograma
  const getAllSelections = () => {
    const combined = {};

    // Agregar selecciones de procedimientos agregados
    addedProcedures.forEach(procedure => {
      if (procedure.selectedTeeth) {
        Object.entries(procedure.selectedTeeth).forEach(([toothLabel, surfaceData]) => {
          if (!combined[toothLabel]) {
            combined[toothLabel] = {};
          }
          Object.entries(surfaceData).forEach(([surface, color]) => {
            // Usar color azul para todos los procedimientos
            combined[toothLabel][surface] = 'blue';
          });
        });
      }
    });

    // Agregar selección actual (temporal) en azul
    Object.entries(selected).forEach(([toothLabel, surfaceData]) => {
      if (!combined[toothLabel]) {
        combined[toothLabel] = {};
      }
      Object.entries(surfaceData).forEach(([surface, color]) => {
        combined[toothLabel][surface] = 'blue';
      });
    });


    return combined;
  };

  // Crear información de bloqueo para el odontograma
  const getBlockedInfo = () => {
    const blocked = {};

    addedProcedures.forEach(procedure => {
      if (procedure.selectedTeeth) {
        Object.entries(procedure.selectedTeeth).forEach(([toothLabel, surfaceData]) => {
          if (!blocked[toothLabel]) {
            blocked[toothLabel] = {};
          }
          Object.entries(surfaceData).forEach(([surface, color]) => {
            blocked[toothLabel][surface] = {
              blocked: true,
              procedureName: procedure.name
            };
          });
        });
      }
    });

    return blocked;
  };



  // Calcular costo total
  useEffect(() => {
    const total = addedProcedures.reduce((sum, proc) => sum + proc.total, 0);
    setTreatmentPlan(prev => ({ ...prev, totalCost: total }));
  }, [addedProcedures]);

  // Guardar plan de tratamiento
  const handleSaveTreatmentPlan = async () => {
    try {
      const token = auth.user?.access_token;

      // Asegurar que todos los IDs de procedimientos sean strings
      const processedProcedures = addedProcedures.map(proc => ({
        ...proc,
        id: proc.id.toString()
      }));

      // Obtener nombre del doctor usando el store de autenticación
      const doctorName = getUserDisplayName();
      const doctorId = getUserId();

      const planData = {
        patientId,
        doctorId: doctorId,
        doctorName: doctorName,
        name: treatmentPlan.name,
        date: treatmentPlan.date,
        selectedProcedures: processedProcedures,
        notes: treatmentPlan.notes,
        totalCost: treatmentPlan.totalCost
      };

      if (isEditMode && planId) {
        // Actualizar plan existente
        await TreatmentPlanService.updateTreatmentPlan(planId, planData, token);
        console.log('Plan de tratamiento actualizado exitosamente');
      } else {
        // Crear nuevo plan
        await TreatmentPlanService.createTreatmentPlan(planData, token);
        console.log('Plan de tratamiento creado exitosamente');
      }

      // Navegar de vuelta a la lista de planes
      navigate(`/patients/${patientId}/treatment-plans`);
    } catch (error) {
      console.error('Error saving treatment plan:', error);
      alert('Error al guardar el plan de tratamiento');
    }
  };

  // Función para manejar la impresión
  const handlePrint = () => {
    try {
      printTreatmentPlan(treatmentPlan, patient, organization?.logoViewUrl, organization, doctor, procedures);
    } catch (error) {
      console.error('Error printing treatment plan:', error);
      alert('Error al imprimir el plan de tratamiento: ' + error.message);
    }
  };

  const handleArchivePlan = async () => {
    if (!planId) {
      return;
    }

    if (!window.confirm(`¿Estás seguro de que quieres archivar el plan "${treatmentPlan.name}"?`)) {
      return;
    }

    try {
      const token = auth.user?.access_token;
      await TreatmentPlanService.deleteTreatmentPlan(planId, token);
      showSuccess('Plan de tratamiento archivado exitosamente');
      navigate(`/patients/${patientId}/treatment-plans`);
    } catch (error) {
      console.error('Error archiving treatment plan:', error);
      showError('Error al archivar el plan de tratamiento');
    }
  };

  if (isLoading) {
    return <div className="loading">Cargando...</div>;
  }

  return (
    <div className={`treatment-plan-container ${isViewMode ? 'view-mode' : ''}`}>
      {/* Header consistente */}
      <PatientSectionHeader
        patientId={patientId}
        patientName={`${patient?.firstName} ${patient?.lastName}`}
        sectionTitle={
          isViewMode ? "Ver Plan de Tratamiento" :
            isEditMode ? "Editar Plan de Tratamiento" :
              "Nuevo Plan de Tratamiento"
        }
        onBackClick={() => navigate(`/patients/${patientId}/treatment-plans`)}
        actionButton={
          isViewMode ? (
            <div className="header-action-buttons">
              <Button
                variant="secondary"
                onClick={handlePrint}
                icon="fas fa-print"
              >
                Imprimir
              </Button>
              <Button
                variant="danger"
                onClick={handleArchivePlan}
                icon="fas fa-archive"
              >
                Archivar
              </Button>
              <Button
                variant="primary"
                onClick={() => navigate(`/patients/${patientId}/treatment-plans/${planId}/edit`)}
                icon="fas fa-edit"
              >
                Editar Plan
              </Button>
            </div>
          ) : (
            <Button
              variant="primary"
              onClick={handleSaveTreatmentPlan}
              disabled={!treatmentPlan.name || addedProcedures.length === 0}
            >
              {isEditMode ? 'Actualizar Plan' : 'Guardar Plan de Tratamiento'}
            </Button>
          )
        }
      />

      <div className="treatment-plan-content">
        {/* Sección de información básica */}
        <div className="basic-info-section">
          <h3>Información del Plan</h3>
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="treatmentName">
                Nombre del Tratamiento
                <small className="field-help">Describe el tipo de tratamiento a realizar</small>
              </label>
              <input
                type="text"
                id="treatmentName"
                value={treatmentPlan.name}
                onChange={(e) => setTreatmentPlan(prev => ({ ...prev, name: e.target.value }))}
                placeholder="Ej: Tratamiento de caries"
                readOnly={isViewMode}
                disabled={isViewMode}
              />
            </div>
            <div className="form-group">
              <label htmlFor="treatmentDate">
                Fecha de Inicio del Tratamiento
                <small className="field-help">Fecha programada para iniciar este plan de tratamiento</small>
              </label>
              <input
                ref={dateInputRef}
                type="date"
                id="treatmentDate"
                value={treatmentPlan.date}
                onChange={(e) => setTreatmentPlan(prev => ({ ...prev, date: e.target.value }))}
                onFocus={handleDateInputFocus}
                onBlur={handleDateInputBlur}
                onClick={handleDateInputClick}
                readOnly={isViewMode}
                disabled={isViewMode}
                autoComplete="off"
              />
            </div>
          </div>
        </div>

        {/* Sección del odontograma */}
        <div className="odontogram-section">
          <h3>Selección de Dientes</h3>
          <p className="section-description">
            {isViewMode
              ? "Dientes y superficies incluidos en este plan de tratamiento"
              : "1. Primero selecciona los dientes y superficies que requieren tratamiento, 2. Luego selecciona el procedimiento y agrégalo"
            }
          </p>


          <div className="mode-switch-odontogram treatment-plan-mode-switch">
            <label className={`mode-option ${odontogramMode === 'adult' ? 'active' : ''}`}>
              <input
                type="radio"
                name="treatment-plan-odontogram-mode"
                value="adult"
                checked={odontogramMode === 'adult'}
                onChange={() => setOdontogramMode('adult')}
              />
              <i className="fas fa-user"></i>
              Adulto
            </label>
            <label className={`mode-option ${odontogramMode === 'pediatric' ? 'active' : ''}`}>
              <input
                type="radio"
                name="treatment-plan-odontogram-mode"
                value="pediatric"
                checked={odontogramMode === 'pediatric'}
                onChange={() => setOdontogramMode('pediatric')}
              />
              <i className="fas fa-child"></i>
              Infantil
            </label>
          </div>
          <div className="odontogram-container">
            <Odontogram
              mode={odontogramMode}
              selected={getAllSelections()}
              blocked={getBlockedInfo()}
              onToggle={isViewMode ? () => { } : toggleSelection}
              isTreatmentPlan={true}
            />
          </div>
          <div className="selected-teeth-summary">
            <h4>Dientes Seleccionados:</h4>
            {Object.keys(getAllSelections()).length > 0 ? (
              <div className="selected-teeth-list">
                {Object.entries(getAllSelections()).map(([tooth, surfaces]) => (
                  <span key={tooth} className="selected-tooth">
                    {tooth} ({Object.keys(surfaces).map(surface => getSurfaceName(tooth, surface)).join(', ')})
                  </span>
                ))}
              </div>
            ) : (
              <p>No hay dientes seleccionados</p>
            )}
          </div>
        </div>

        {/* Sección de procedimientos simplificada */}
        <div className="procedures-section">
          <div className="procedures-header">
            <h3>Procedimientos</h3>
            {!isViewMode && (
              <Button
                variant="primary"
                size="small"
                onClick={handleAddProcedure}
                icon="fas fa-plus"
                disabled={Object.keys(selected).length === 0}
                title={Object.keys(selected).length === 0 ? "Primero selecciona dientes en el odontograma" : "Agregar procedimiento"}
              >
                Agregar Procedimiento
              </Button>
            )}
          </div>

          {/* Lista simple de procedimientos - solo visual */}
          {addedProcedures.length > 0 ? (
            <div className="procedures-simple-list">
              {addedProcedures.map((proc) => {
                const subtotal = (Number(proc.amount) || 0) * (Number(proc.quantity) || 0);
                const hasDiscount = proc.discountType && (proc.discountValue !== '' && proc.discountValue != null);
                const discountAmount = hasDiscount
                  ? proc.discountType === 'amount'
                    ? Math.min(Number(proc.discountValue) || 0, subtotal)
                    : subtotal * (Math.min(Math.max(Number(proc.discountValue) || 0, 0), 100) / 100)
                  : 0;
                const discountLabel = hasDiscount
                  ? proc.discountType === 'amount'
                    ? `-$${discountAmount.toFixed(2)}`
                    : `${Number(proc.discountValue)}%`
                  : null;
                return (
                <div key={proc.id} className="procedure-simple-item">
                  <div className="procedure-simple-header">
                    <div className="procedure-main-info">
                      <span className="procedure-name">{proc.name}</span>
                      <div className="procedure-stats">
                        <span className="stat-item">
                          <strong>Costo:</strong> ${Number(proc.amount).toFixed(2)}
                        </span>
                        <span className="stat-item">
                          <strong>Cantidad:</strong> {proc.quantity}
                        </span>
                        {hasDiscount && (
                          <>
                            <span className="stat-item">
                              <strong>Subtotal:</strong> ${subtotal.toFixed(2)}
                            </span>
                            <span className="stat-item stat-item-discount">
                              <strong>Descuento:</strong> {discountLabel}
                            </span>
                          </>
                        )}
                        <span className="stat-item total-stat">
                          <strong>Total:</strong> ${Number(proc.total).toFixed(2)}
                        </span>
                      </div>
                    </div>
                    {!isViewMode && (
                      <div className="procedure-actions">
                        <button
                          className="action-btn edit-btn"
                          onClick={() => handleEditProcedure(proc)}
                          title="Editar"
                        >
                          <i className="fas fa-edit"></i>
                        </button>
                        <button
                          className="action-btn remove-btn"
                          onClick={() => handleRemoveProcedure(proc.id)}
                          title="Eliminar"
                        >
                          <i className="fas fa-times"></i>
                        </button>
                      </div>
                    )}
                  </div>

                  {proc.selectedTeeth && Object.keys(proc.selectedTeeth).length > 0 && (
                    <div className="procedure-teeth-display">
                      <strong>Dientes:</strong>
                      <div className="procedure-teeth-list">
                        {Object.entries(proc.selectedTeeth).map(([tooth, surfaces]) => (
                          <span key={tooth} className="procedure-tooth">
                            {tooth} ({Object.keys(surfaces).map(surface => getSurfaceName(tooth, surface)).join(', ')})
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {proc.notes && (
                    <div className="procedure-notes-display">
                      <strong>Notas:</strong> {proc.notes}
                    </div>
                  )}
                </div>
              );
              })}
            </div>
          ) : (
            <div className="no-procedures">
              <p>No hay procedimientos agregados</p>
            </div>
          )}
        </div>

        {/* Resumen y total */}
        <div className="summary-section">
          <div className="cost-summary">
            <h3>Resumen de Costos</h3>
            <div className="total-cost">
              <span className="total-label">Costo Total:</span>
              <span className="total-amount">${Number(treatmentPlan.totalCost).toFixed(2)}</span>
            </div>
          </div>
        </div>

        {/* Sección de notas generales */}
        <div className="notes-section">
          <h3>Notas Generales</h3>
          <div className="form-group">
            <textarea
              value={treatmentPlan.notes}
              onChange={(e) => setTreatmentPlan(prev => ({ ...prev, notes: e.target.value }))}
              placeholder="Agregar notas generales del plan de tratamiento..."
              rows="4"
              readOnly={isViewMode}
              disabled={isViewMode}
            />
          </div>
        </div>

        {/* Botones de acción movidos arriba */}
      </div>

      {/* Modal para agregar/editar procedimientos */}
      {isModalOpen && (
        <div className="modal-overlay" onClick={() => setIsModalOpen(false)}>
          <div className="modal-content treatment-plan-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{editingProcedure ? 'Editar' : 'Agregar'} Procedimiento</h3>
              <button
                className="modal-close"
                onClick={() => setIsModalOpen(false)}
              >
                <i className="fas fa-times"></i>
              </button>
            </div>

            <div className="modal-body">
              <div className="form-group">
                <label>Procedimiento:</label>
                <SimpleProcedureDropdown
                  procedures={procedures}
                  selectedProcedure={procedures.find(p => p.id === modalProcedure.procedureId)}
                  onSelect={handleProcedureSelect}
                  placeholder="Buscar y seleccionar procedimiento..."
                  disabled={isLoadingProcedures}
                />
              </div>

              <div className="form-row compact">
                <div className="form-group">
                  <label>Costo:</label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={modalProcedure.amount || ''}
                    onChange={(e) => {
                      const amount = e.target.value === '' ? '' : parseFloat(e.target.value) || 0;
                      setModalProcedure(prev => {
                        const next = { ...prev, amount };
                        const { total } = getModalTotals(next);
                        return { ...next, total };
                      });
                    }}
                    placeholder="0.0"
                  />
                </div>
                <div className="form-group">
                  <label>Cantidad:</label>
                  <input
                    type="number"
                    min="1"
                    value={modalProcedure.quantity || ''}
                    onChange={(e) => handleQuantityChange(e.target.value)}
                    placeholder="1"
                  />
                </div>
                <div className="form-group">
                  <label>Subtotal:</label>
                  <input
                    type="text"
                    value={`$${getModalTotals(modalProcedure).subtotal.toFixed(2)}`}
                    readOnly
                    className="total-display total-subtotal"
                  />
                </div>
              </div>

              {/* Sección Descuento */}
              <div className="procedure-discount-section">
                <label className="discount-section-label">Descuento</label>
                <div className="discount-type-tabs">
                  <button
                    type="button"
                    className={`discount-tab ${!modalProcedure.discountType ? 'active' : ''}`}
                    onClick={() => handleDiscountChange('', '')}
                  >
                    Ninguno
                  </button>
                  <button
                    type="button"
                    className={`discount-tab ${modalProcedure.discountType === 'amount' ? 'active' : ''}`}
                    onClick={() => handleDiscountChange('amount', modalProcedure.discountType === 'amount' ? modalProcedure.discountValue : '')}
                  >
                    Por monto
                  </button>
                  <button
                    type="button"
                    className={`discount-tab ${modalProcedure.discountType === 'percent' ? 'active' : ''}`}
                    onClick={() => handleDiscountChange('percent', modalProcedure.discountType === 'percent' ? modalProcedure.discountValue : '')}
                  >
                    Por %
                  </button>
                </div>
                {(modalProcedure.discountType === 'amount' || modalProcedure.discountType === 'percent') && (
                  <div className="discount-value-row">
                    <span className="discount-unit" aria-hidden="true">
                      {modalProcedure.discountType === 'amount' ? '$' : '%'}
                    </span>
                    <input
                      type="number"
                      min="0"
                      max={modalProcedure.discountType === 'percent' ? 100 : undefined}
                      step={modalProcedure.discountType === 'amount' ? 0.01 : 0.5}
                      value={modalProcedure.discountValue ?? ''}
                      onChange={(e) => {
                        const val = e.target.value === '' ? '' : parseFloat(e.target.value) || 0;
                        handleDiscountChange(modalProcedure.discountType, val);
                      }}
                      placeholder={modalProcedure.discountType === 'amount' ? '0.00' : '0'}
                      className="discount-value-input"
                    />
                  </div>
                )}
              </div>

              {/* Resumen: Subtotal → Descuento → Total */}
              <div className="procedure-total-summary">
                {getModalTotals(modalProcedure).discountAmount > 0 && (
                  <div className="summary-line summary-discount">
                    <span>Descuento:</span>
                    <span className="discount-amount">-${getModalTotals(modalProcedure).discountAmount.toFixed(2)}</span>
                  </div>
                )}
                <div className="summary-line summary-total">
                  <span>Total:</span>
                  <span className="total-final">${Number(modalProcedure.total).toFixed(2)}</span>
                </div>
              </div>

              <div className="form-group">
                <label>Notas:</label>
                <textarea
                  value={modalProcedure.notes}
                  onChange={(e) => setModalProcedure(prev => ({ ...prev, notes: e.target.value }))}
                  placeholder="Notas del procedimiento..."
                  rows="2"
                />
              </div>
            </div>

            <div className="modal-footer">
              <Button
                variant="outline"
                onClick={() => setIsModalOpen(false)}
              >
                Cancelar
              </Button>
              <Button
                variant="primary"
                onClick={handleSaveProcedure}
                disabled={!modalProcedure.procedureId}
              >
                {editingProcedure ? 'Actualizar' : 'Agregar'}
              </Button>
            </div>
          </div>
        </div>
      )}
      <ToastContainer toasts={toasts} removeToast={removeToast} />
    </div>
  );
};

export default TreatmentPlan;
